// Hızlı Satış Ekranı (kasa) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'sales.quickSaleScreen';

export const quickSaleScreenGuide = (t: TFunction): GuideContent => ({
  layout: 'split',
  title: t(`${G}.title`, 'Hızlı satış ekranı nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Kasada satış bu ekrandan yapılır: ürünü sepete ekleyin, gerekirse indirim veya not girin, ödeme düğmesiyle satışı kapatın. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Üst çubuk'),
    list: t(`${G}.regions.list`, 'Cari, barkod ve sepet'),
    form: t(`${G}.regions.form`, 'Ürünler ve işlem tuşları'),
    footer: t(`${G}.regions.footer`, 'Ödeme düğmeleri'),
  },
  steps: [
    {
      region: 'list',
      title: t(`${G}.s1.title`, '1. Ürünü sepete ekleyin'),
      text: t(`${G}.s1.text`, 'Barkodu okutun ya da "Ürün Adı/Barkod ile Ara" kutusuna yazıp Enter\'a basın; sağdaki ürün kartlarına dokunarak da ekleyebilirsiniz. Üstte satışın yazılacağı cari seçilidir; isimsiz satışlar "Genel Cari"ye yazılır.'),
      tips: [
        t(`${G}.s1.tip1`, 'Miktarı değiştirmek için sepette ürünü seçin, "Miktar"a basın, rakamı girin ve Enter\'a basın.'),
        t(`${G}.s1.tip2`, '"Arttır" / "Azalt" seçili ürünün adedini bir değiştirir, "Sil" ürünü sepetten çıkarır, "Sepeti Temizle" onay sorup tüm sepeti boşaltır.'),
        t(`${G}.s1.tip3`, 'Cari aramak için "Cari Ara/Seç", yeni müşteri için "Yeni Cari Kayıt" kullanın.'),
      ],
    },
    {
      region: 'form',
      title: t(`${G}.s2.title`, '2. İndirim, açıklama ve fiş notu'),
      text: t(`${G}.s2.text`, '"İndirim" ile sepete yüzde (%) ya da tutar (TL) olarak indirim uygulayın; bu düğme yalnız indirim yetkisi olan personelde çalışır. Sepette bir ürün seçip "Açıklama"ya basarak ürüne not ekleyin (ör. "acısız"). Tüm fişe not için cari alanındaki "Fişe Not Ekle"yi kullanın.'),
    },
    {
      region: 'form',
      title: t(`${G}.s3.title`, '3. Beklemeye al ve sepeti böl'),
      text: t(`${G}.s3.text`, '"Beklemeye Al" sepeti ve seçili cariyi kenara koyar, ekranı yeni müşteriye açar; "Bekleyenler"den geri çağırırsınız. "Sepet Böl" bir sepeti birden fazla fişe ayırır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Bekleyen satışlar yalnız bu bilgisayarın tarayıcısında saklanır; başka kasadan görünmez.'),
        t(`${G}.s3.tip2`, 'Bekleyen satışı geri çağırdığınızda o anki sepetin yerine geçer; önce açık sepeti bitirin.'),
        t(`${G}.s3.tip3`, 'Sepet toplamı yazarkasanın tek fiş limitini aşarsa ödeme alınmaz ve "Mali Fiş Limiti Aşıldı" uyarısı çıkar; "Sepet Böl" ile birkaç fişe ayırın.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s4.title`, '4. Ödemeyi alın'),
      text: t(`${G}.s4.text`, 'Nakit düğmesi satışı nakit olarak kapatır, "POS" kartla ödeme başlatır (birden fazla POS tanımlıysa ödeme penceresi açılır, POS orada seçilir). "Açık Hesap" yalnız Genel Cari dışında bir cari seçiliyken ve ayarlarda açıksa görünür: para alınmaz, satış o cariye borç olarak yazılır. "Diğer Ödeme" ve "Online Ödeme" ayarlarda tanımlıysa görünür.'),
      tips: [
        t(`${G}.s4.tip1`, 'Ekran ayarlarında para üstü penceresi açıksa nakitte önce alınan para sorulur ve para üstü gösterilir.'),
        t(`${G}.s4.tip2`, 'Nakit ve kart karışık ödemede "Parçalı Ödeme"yi kullanın.'),
        t(`${G}.s4.tip3`, 'Ödeme sırasında düğmeler kilitlenir; işlem bitene kadar ikinci kez basmayın.'),
        t(`${G}.s4.tip4`, '"POS Ayarı Eksik" uyarısı çıkarsa o POS\'a ekran ayarlarından banka hesabı atanmamıştır; yöneticinize haber verin.'),
      ],
    },
    {
      region: 'form',
      title: t(`${G}.s5.title`, '5. İade ve yazarkasa uyarıları'),
      text: t(`${G}.s5.text`, '"POS İade" yalnız bekleyen bir kart iadesi varken açılır: parçalı ödemede silinen POS ödemeleri burada listelenir ve iadesi POS cihazına gönderilir. Yazarkasa bağlı değilse veya fiş kesilemezse ekranda uyarı penceresi çıkar; penceredeki (i) düğmesi seçeneklerin ne yaptığını anlatır.'),
      tips: [
        t(`${G}.s5.tip1`, 'Üst çubuktaki yazarkasa göstergesi (Inpos / Beko) yeşilse bağlı, kırmızıysa bağlı değildir.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s6.title`, '6. Mutfak siparişleri ve menü'),
      text: t(`${G}.s6.text`, 'Mutfak ekranı açıksa üst çubukta "Mutfak" göstergesi görünür (yeşil: bağlı, kırmızı: bağlı değil). Ona basınca "Mutfak Siparişleri" açılır: siparişi seçip "HAZIR", sonra "TESLİM EDİLDİ" ile durumunu ilerletir, "Fiş Yazdır" ile fişini basarsınız. "Menü"de Ekran Arayüz Ayarları, Ürün Kartı Düzeni, Gün Sonu ve Güvenli Çıkış bulunur.'),
    },
    {
      region: 'header',
      title: t(`${G}.s7.title`, '7. Kısayol tuşları'),
      text: t(`${G}.s7.text`, 'F11 tam ekranı açar/kapatır. Nakit, POS, parçalı ödeme, indirim, beklemeye al, sepet böl, adet artır/azalt ve sepeti temizle için tuşlar, yerel yazdırma servisinin "Seçenekler > Ayarlar" sayfasından atanır; servis çalışmıyorsa kısayollar devre dışıdır.'),
      tips: [
        t(`${G}.s7.tip1`, 'Kısayolla ödeme onay sormadan satışı kapatır; düğmenin pasif olduğu durumda (boş sepet, ödeme sürerken) kısayol da çalışmaz.'),
        t(`${G}.s7.tip2`, 'Sayısal tuş takımı yalnız "Miktar" modu açıkken adet girer.'),
      ],
    },
  ],
});
